// components/tokens/LoadAotoken.js

import { useState, useEffect, useCallback } from 'react';
import Web3 from 'web3';
import { contractABI, contractAddress } from '../../contractConfig';
import { Link } from 'react-router-dom';
import '../../loadStyle.css';

const LoadAotoken = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [web3, setWeb3] = useState(null);
  const [contractURI, setContractURI] = useState(null);
  const [tokensVisible, setTokensVisible] = useState(false); // Toggle tokens visibility

// ✅ Use the Pinata public IPFS gateway
const replaceIPFSLink = useCallback((url) => {
  if (!url) return '';

  url = url.trim();

  // Keep ordinary HTTP/HTTPS links unchanged
  if (
    (url.startsWith('https://') || url.startsWith('http://')) &&
    !url.includes('/ipfs/')
  ) {
    return url;
  }

  // Extract the CID/path from any IPFS URI or gateway URL
  const cid = url.includes('/ipfs/')
    ? url.split('/ipfs/')[1]
    : url
        .replace(/^ipfs:\/\/ipfs\//i, '')
        .replace(/^ipfs:\/\//i, '');

  return `https://gateway.pinata.cloud/ipfs/${cid}`;
}, []);

  // 👇 use public RPC instead of MetaMask
  useEffect(() => {
    const rpcURL = "https://polygon-amoy.drpc.org"; // Amoy testnet
    const web3Instance = new Web3(new Web3.providers.HttpProvider(rpcURL));
    setWeb3(web3Instance);
  }, []);

  const fetchContractURI = useCallback(
    async (contract) => {
      try {
        const contractURI = await contract.methods.contractURI().call();
        const formattedContractURI = replaceIPFSLink(contractURI);
        const response = await fetch(formattedContractURI);

        if (!response.ok) throw new Error(`Failed to fetch contract metadata: ${response.statusText}`);

        const contractMetadata = await response.json();
        contractMetadata.image = replaceIPFSLink(contractMetadata.image);
        contractMetadata.external_link = replaceIPFSLink(contractMetadata.external_link);
        setContractURI(contractMetadata);
      } catch (error) {
        console.error('Error fetching contract URI:', error);
        setError('Error fetching contract URI: ' + error.message);
      }
    },
    [replaceIPFSLink]
  );

  const fetchTokens = useCallback(
    async (contract) => {
      try {
        const tokensArray = [];
        const totalMintedTokens = await contract.methods.totalMintedTokens().call();

        for (let i = 1; i <= Number(totalMintedTokens); i++) {
          try {
            const tokenURI = await contract.methods.tokenURI(i).call();
            const formattedTokenURI = replaceIPFSLink(tokenURI);
            const response = await fetch(formattedTokenURI);

            if (!response.ok) throw new Error(`Failed to fetch token metadata: ${response.statusText}`);

            const metadata = await response.json();
            metadata.image = replaceIPFSLink(metadata.image);
            metadata.external_link = replaceIPFSLink(metadata.external_link);

            // Fetch request details incl. approvalCount
            const request = await contract.methods.mintRequests(i).call();
            const approvalCount = parseInt(request.approvalCount, 10);

            tokensArray.push({ tokenId: i, approvalCount, ...metadata });
          } catch (error) {
            console.error('Error fetching token metadata:', error);
          }
        }

        setTokens(tokensArray);
      } catch (error) {
        console.error('Error fetching tokens:', error);
        setError('Error fetching tokens: ' + error.message);
      } finally {
        setLoading(false);
      }
    },
    [replaceIPFSLink]
  );

  // 👇 run only when web3 is ready
  useEffect(() => {
    if (web3) {
      const contract = new web3.eth.Contract(contractABI, contractAddress);
      fetchContractURI(contract);
      fetchTokens(contract);
    }
  }, [web3, fetchContractURI, fetchTokens]);

    const toggleTokensVisibility = () => {
    setTokensVisible((prev) => !prev);
  };

  return (
    <div className="container">
      <h2>Authorisation Program and Digital Licences</h2> {/* Always shown */}

      {/* Show loading or error messages below the heading */}
      {loading && <p>Loading created licences...</p>}
      {error && <p>{error}</p>}

      {/* Only render collection/tokens if not loading and no error */}
      {!loading && !error && (
        <>
        {contractURI && (
          <div className="collection-container">
            <section className="collection-profile">
              <h3>
                <div onClick={toggleTokensVisibility} style={{ cursor: 'pointer' }}>
                  Authorisation Program:
                  <span style={{ marginLeft: '5px' }}>{tokens.length} licences</span>
                  <span style={{ marginLeft: '5px' }}>
                    {tokensVisible ? '[-]' : '[+]'}
                  </span>
                </div>
              </h3>

              {!tokensVisible && (
                <>
                  <p><strong>Name:</strong> {contractURI.name}</p>
                  <p><strong>Description:</strong> {contractURI.description}</p>

                  {contractURI.image && (
                    <div>
                      <strong>Image:</strong>
                      <img
                        className="collection-image"
                        src={contractURI.image}
                        alt={contractURI.name}
                      />
                    </div>
                  )}

                  {contractURI.external_link && (
                    <p>
                      <strong>External Link:</strong>{' '}
                      <a href={contractURI.external_link} target="_blank" rel="noopener noreferrer">
                        {contractURI.external_link}
                      </a>
                    </p>
                  )}
                </>
              )}
            </section>
          </div>
        )}

        {tokensVisible && (
          <div className="tokens-container">
            <section className="tokens-archive">
              <h3>Issued Licences</h3>
              {tokens.length === 0 ? (
                <p>No tokens minted yet.</p>
              ) : (
                <div className="tokens-container">
                  {tokens.map((token) => (
                    <div className="tokens-item" key={token.tokenId}>
                      <Link to={`/tokens/${token.tokenId}`} className="tokens-link">
                        <p><strong>Licence ID:</strong> {token.tokenId}</p>
                        <p><strong>Multisig Approval:</strong> {token.approvalCount} Signers</p>
                        <p><strong>Licence Name:</strong> {token.name}</p>
                        <p className="description">
                          <strong>Licence Description:</strong> {token.description}
                        </p>
                        {token.image && (
                          <div>
                            <strong>Licence Image:</strong>
                            <img
                              className="tokens-image"
                              src={token.image}
                              alt={token.name}
                            />
                          </div>
                        )}
                      </Link>
                    </div>
                  ))}
                </div>
              )}
              </section>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LoadAotoken;